<template>
  <div
    class="play-radio-group"
    :class="[{ 'play-radio-group--stacked': stacked, 'is-error': !!error }]"
    role="radiogroup"
  >
    <PlayRadio
      v-for="opt in normalizedOptions"
      :key="`${opt.value}`"
      :value="opt.value"
      :checked="value"
      :name="groupName"
      :variant="variant"
      :disabled="disabled || opt.disabled"
      @change="$emit('input', $event)"
    >
      {{ opt.text }}
    </PlayRadio>

    <p
      v-if="error"
      class="play-radio-group__error"
    >
      {{ error }}
    </p>
  </div>
</template>

<script>
import PlayRadio from '@/components/play/PlayRadio.vue'

/**
 * PlayRadioGroup — options-driven radio group for the Play system.
 *
 * Token-driven, composed of <PlayRadio>. Replaces bootstrap-vue's
 * <b-form-radio-group> + `:options`. Accepts the same shorthand option shapes
 * (array of strings, or `{ text, value, disabled }` objects). Drive the
 * selected value with v-model; `stacked` lays items vertically.
 *
 * VUE3-MIGRATION: `model: { prop: 'value', event: 'input' }` — Vue 3 uses
 * `modelValue` / `update:modelValue`. Change this block + the emit name on
 * migration.
 */
let uid = 0

export default {
  name: 'PlayRadioGroup',
  components: { PlayRadio },
  model: {
    prop: 'value',
    event: 'input',
  },
  props: {
    /** Selected value (v-model target) */
    value: {
      type: [String, Number, Boolean],
      default: '',
    },
    /** ['a','b'] or [{ text, value, disabled }] */
    options: {
      type: Array,
      default: () => [],
    },
    /** Shared radio name; auto-generated if omitted */
    name: {
      type: String,
      default: '',
    },
    variant: {
      type: String,
      default: 'primary',
    },
    /** Lay items vertically instead of inline */
    stacked: {
      type: Boolean,
      default: false,
    },
    error: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    uid += 1
    return {
      generatedName: `play-radio-group-${uid}`,
    }
  },
  computed: {
    groupName() {
      return this.name || this.generatedName
    },
    normalizedOptions() {
      return this.options.map(opt => (
        typeof opt === 'object' && opt !== null
          ? { text: opt.text ?? opt.value, value: opt.value, disabled: !!opt.disabled }
          : { text: opt, value: opt, disabled: false }
      ))
    },
  },
}
</script>

<style lang="scss" scoped>
.play-radio-group {
  display: flex;
  flex-wrap: wrap;
  gap: 14px 20px;

  &--stacked {
    flex-direction: column;
    gap: 12px;
  }

  &__error {
    flex-basis: 100%;
    margin: 2px 0 0;
    font-family: var(--font-body);
    font-size: 12.5px;
    font-weight: 500;
    color: var(--c-danger-strong);
  }
}
</style>
